'use client';
import type {
  BlogCardGridFragment as BlogCardGridQueryResponse,
  ContactCardGridFragment as ContactCardGridQueryResponse,
  DirectBookingFragment as DirectBookingQueryResponse,
  QuotesFragment as QuoteQueryResponse,
  TextImageBackgroundFragment as TextImageBackgroundQueryResponse,
} from '@mnd-apps/public/wp-generated/types';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { Fragment, useState } from 'react';
import { useSetLanguageLinks } from '../contexts/LanguageLinksContext';
import type { HubspotFormConfiguration } from '../lib/getHubspotForm';
import { shouldIgnoreMenuRender } from '../lib/menus';
import type {
  CustomerCasePageFragment,
  GlobalModulesFragment,
  PageFragment,
  PostPageFragment,
} from '../wp-generated/types';
import GlobalModule from './modules/GlobalModule';
import HeroSelector from './modules/HeroModule';
import NewsLetterSignupModule from './modules/NewsletterSignupModule';
import Notice from './modules/Notice';
import type { StatCardGridModuleQueryResponseType } from './modules/StatCardGridModule/index.type';

interface CmsPageAppRouterProps {
  locale: string;
  page: PageFragment | CustomerCasePageFragment | PostPageFragment | null;
  hubspotFormConfig?: HubspotFormConfiguration; //Passed as prop in order to fetch in a server component
}

const BlogCardGridModule = dynamic(() => import('./modules/BlogCardGridModule'));
const ContactCardGridModule = dynamic(() => import('./modules/ContactCardGridModule'));
const DirectBookingModule = dynamic(() => import('./modules/DirectBookingModule'));
const TextImageBackground = dynamic(
  () => import('./modules/TextImageBackgroundModule/TextImageBackgroundModule'),
);
const TextMediaSectionModule = dynamic(() => import('./modules/TextMediaSectionModule'));
const TextBlocksModule = dynamic(() => import('./modules/TextBlocksModule'));
const LogosModuleOld = dynamic(() => import('./modules/LogosModule'));
const LogosWallModule = dynamic(() => import('./modules/LogosModule/2025design'));
const QuotesModule = dynamic(() => import('./modules/QuotesModule'));
const StatCardGridModule = dynamic(() => import('./modules/StatCardGridModule'));
const TestimonialsModule = dynamic(() => import('./modules/TestimonialsModule'));
const NumbersModule = dynamic(() => import('./modules/NumbersModule'));
const LatestPostsModule = dynamic(() => import('./modules/LatestPostsModule'));
const EmployeesModule = dynamic(() => import('./modules/EmployeesModule'));
const FeaturedPages = dynamic(() =>
  import('./modules/FeaturedModule').then(module => ({
    default: module.FeaturedPages,
  })),
);
const FeaturedPosts = dynamic(() =>
  import('./modules/FeaturedModule').then(module => ({
    default: module.FeaturedPosts,
  })),
);
const Reviews = dynamic(() => import('./modules/ReviewsModule'));
const Faq = dynamic(() => import('./modules/FaqModule/2025design'));
const FaqOld = dynamic(() => import('./modules/FaqModule/FaqModule'));
const PricingPlansOld = dynamic(() => import('./modules/PricingPlansModule/PricingPlansModule'));
const PricingPlans = dynamic(() => import('./modules/PricingPlansModule/2025design'));
const ComparisonTable = dynamic(
  () => import('./modules/ComparisonTableModule/ComparisonTableModule'),
);
const VideoOld = dynamic(() => import('./modules/VideoModule'));
const VideoModule = dynamic(() => import('./modules/VideoModule/2025design/VideoModule'));
const CustomerCasesModule = dynamic(() => import('./modules/CustomerCasesModule'));
const WaveModule = dynamic(() => import('./modules/WaveModule'));
const LatestResources = dynamic(() => import('./modules/LatestResources'));
const TrialSignupModule = dynamic(() => import('./modules/TrialSignupModule'));
const RelatedPosts = dynamic(() => import('./modules/RelatedPosts'));
const IframeModule = dynamic(() => import('./modules/IframeModule'));
const PageAnchor = dynamic(() => import('./modules/PageAnchor'));
const PrReportIntro = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/intro'),
);
const ChapterOne = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/chapter-one'),
);
const ChapterTwo = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/chapter-two'),
);
const ChapterThree = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/chapter-three'),
);
const ChapterFour = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/chapter-four'),
);
const ChapterFive = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/chapter-five'),
);
const PrReportSummary = dynamic(
  () => import('../app/[locale]/[type]/[[...pages]]/pr-report/components/summary'),
);
const MissingModule = dynamic(() => import('./modules/MissingModule'));
const BreadcrumbsComponent = dynamic(() => import('./Breadcrumbs'), {
  loading: () => <p>Loading...</p>,
});
const ArchiveItemPage = dynamic(() => import('./ArchiveItemPage'));
const FeatureCardGrid = dynamic(() => import('./modules/FeatureCardGridModule'));

export const MODULE_FLEXIBLE = 'ModulesFlexibleModules';

export function getModule(
  moduleData:
    | NonNullable<NonNullable<PageFragment['modules']>['flexibleModules']>[0]
    | NonNullable<GlobalModulesFragment['data']>[0],
  locale?: string,
  hero?: PageFragment['hero'],
) {
  switch (moduleData?.__typename) {
    case `${MODULE_FLEXIBLE}TextBlocksLayout`:
      return moduleData.use2025Design ? (
        <div>new design (TextBlocksModule)</div>
      ) : (
        <TextBlocksModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}BlogCardGridLayout`:
      return <BlogCardGridModule moduleData={moduleData as BlogCardGridQueryResponse} />;

    case `${MODULE_FLEXIBLE}ContactCardGridLayout`:
      return <ContactCardGridModule moduleData={moduleData as ContactCardGridQueryResponse} />;

    case `${MODULE_FLEXIBLE}DirectBookingLayout`:
      return <DirectBookingModule moduleData={moduleData as DirectBookingQueryResponse} />;

    case `${MODULE_FLEXIBLE}TextImageBackgroundLayout`:
      return <TextImageBackground moduleData={moduleData as TextImageBackgroundQueryResponse} />;

    case `${MODULE_FLEXIBLE}TextMediaSectionLayout`:
      return <TextMediaSectionModule moduleData={moduleData} />;

    case `${MODULE_FLEXIBLE}LogosLayout`:
      return moduleData.use2025Design ? (
        <LogosWallModule moduleData={moduleData} />
      ) : (
        <LogosModuleOld moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}QuotesLayout`:
      return <QuotesModule moduleData={moduleData as QuoteQueryResponse} />;

    // Will be deprecated after 2025 design is fully implemented
    case `${MODULE_FLEXIBLE}TestimonialsLayout`:
      return <TestimonialsModule moduleData={moduleData} />;

    case `${MODULE_FLEXIBLE}StatCardGridLayout`:
      return <StatCardGridModule moduleData={moduleData as StatCardGridModuleQueryResponseType} />;

    // Will be deprecated after 2025 design is fully implemented
    case `${MODULE_FLEXIBLE}NumbersLayout`:
      return <NumbersModule moduleData={moduleData} />;

    case `${MODULE_FLEXIBLE}LatestPostsLayout`:
      return moduleData.use2025Design ? (
        <div>new design (LatestPostsModule)</div>
      ) : (
        <LatestPostsModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}EmployeesLayout`:
      return moduleData.use2025Design ? (
        <div>new design (EmployeesModule)</div>
      ) : (
        <EmployeesModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}FeaturedPagesLayout`:
      return moduleData.use2025Design ? (
        <div>new design (FeaturedPages)</div>
      ) : (
        <FeaturedPages moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}FeaturedPostsLayout`:
      return moduleData.use2025Design ? (
        <div>new design (FeaturedPosts)</div>
      ) : (
        <FeaturedPosts moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}ReviewsLayout`:
      return moduleData.use2025Design ? (
        <div>new design (Reviews)</div>
      ) : (
        <Reviews moduleData={moduleData} locale={locale} />
      );

    case `${MODULE_FLEXIBLE}FaqLayout`:
      return moduleData.use2025Design ? (
        <Faq moduleData={moduleData} />
      ) : (
        <FaqOld moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}PricingPlansLayout`:
      return moduleData.use2025Design ? (
        <PricingPlans moduleData={moduleData} />
      ) : (
        <PricingPlansOld moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}ComparisonTableLayout`:
      return moduleData.use2025Design ? (
        <div>new design (ComparisonTable)</div>
      ) : (
        <ComparisonTable moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}VideoLayout`:
      return moduleData.use2025Design ? (
        <VideoModule moduleData={moduleData} />
      ) : (
        <VideoOld moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}FeaturedCustomerCasesLayout`:
      return moduleData.use2025Design ? (
        <div>new design (CustomerCasesModule)</div>
      ) : (
        <CustomerCasesModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}WaveLayout`:
      return moduleData.use2025Design ? (
        <div>new design (WaveModule)</div>
      ) : (
        <WaveModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}LatestResourcesLayout`:
      return moduleData.use2025Design ? (
        <div>new design (LatestResources)</div>
      ) : (
        <LatestResources moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}TrialSignupFormLayout`:
      return moduleData.use2025Design ? (
        <div>new design (TrialSignupModule)</div>
      ) : (
        <TrialSignupModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}RelatedPostsLayout`:
      return moduleData.use2025Design ? (
        <div>new design (RelatedPosts)</div>
      ) : (
        <RelatedPosts moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}IframeLayout`:
      return moduleData.use2025Design ? (
        <div>new design (IframeModule)</div>
      ) : (
        <IframeModule moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}PageAnchorLayout`:
      return moduleData.use2025Design ? (
        <div>new design (PageAnchor)</div>
      ) : (
        <PageAnchor moduleData={moduleData} />
      );

    case `${MODULE_FLEXIBLE}PrReportIntroLayout`:
      return <PrReportIntro moduleData={moduleData} hero={hero?.heroPrReport} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportChapterOneLayout`:
      return <ChapterOne moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportChapterTwoLayout`:
      return <ChapterTwo moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportChapterThreeLayout`:
      return <ChapterThree moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportChapterFourLayout`:
      return <ChapterFour moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportChapterFiveLayout`:
      return <ChapterFive moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}PrReportSummaryLayout`:
      return <PrReportSummary moduleData={moduleData} locale={locale} />;

    case `${MODULE_FLEXIBLE}FeatureCardGridLayout`:
      return <FeatureCardGrid data={moduleData} />;

    case `${MODULE_FLEXIBLE}NewsletterLayout`:
      return <NewsLetterSignupModule moduleData={moduleData} />;

    default:
      return <MissingModule typename={moduleData?.__typename ?? 'unknown'} details={moduleData} />;
  }
}

export default function CmsPageAppRouter({
  locale,
  page,
  hubspotFormConfig,
}: CmsPageAppRouterProps) {
  const pathname = usePathname();
  const [noticeIsOpen, setNoticeIsOpen] = useState(true);
  const isFrontPage = page && 'isFrontPage' in page && page.isFrontPage;
  useSetLanguageLinks(isFrontPage ? [] : page && 'translations' in page ? page.translations : []);
  if (page === null) {
    return null;
  }
  return (
    <main>
      {page.__typename === 'Page' ? (
        <>
          {page.stickyModules?.notice?.title && noticeIsOpen && (
            <Notice
              notice={page.stickyModules?.notice}
              noticeIsOpen={noticeIsOpen}
              setNoticeIsOpen={setNoticeIsOpen}
            />
          )}
          {(page.seo?.breadcrumbs?.length || 0) > 1 &&
            !isFrontPage &&
            !shouldIgnoreMenuRender(pathname) && (
              <BreadcrumbsComponent
                breadcrumbs={
                  (page?.seo?.breadcrumbs?.filter(b => b?.url && b?.text) as {
                    url: string;
                    text: string;
                  }[]) ?? []
                }
                hero={page.hero}
              />
            )}
          <HeroSelector
            hero={page.hero}
            pageName={page.title ?? ''}
            noticeIsOpen={!!page.stickyModules?.notice?.title && noticeIsOpen}
            hubspotFormConfig={hubspotFormConfig}
          />
          {page.modules?.flexibleModules?.map((moduleData, index) => {
            if (moduleData?.__typename === `${MODULE_FLEXIBLE}GlobalModuleLayout`) {
              return <GlobalModule key={index} moduleData={moduleData} locale={locale} />;
            } else if (moduleData?.__typename === `${MODULE_FLEXIBLE}TextBlockLayout`) {
              return moduleData.use2025Design ? (
                <div key={index}>New design placeholder ModulesFlexibleModulesTextBlockLayout</div>
              ) : (
                <TextBlocksModule key={index} moduleData={moduleData} />
              );
            } else {
              return <Fragment key={index}>{getModule(moduleData, locale, page.hero)}</Fragment>;
            }
          })}
        </>
      ) : page.__typename === 'CustomerCase' || page.__typename === 'Post' ? (
        <ArchiveItemPage locale={locale} page={page} />
      ) : null}
    </main>
  );
}
